import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Search,
  ChevronDown,
  LogOut,
  User,
  Shield,
  Lock,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UserRole, RoleDisplayNames } from '@/types/global.types';
import { NotificationBell } from '../NotificationBell';

interface HeaderProps {
  onMenuClick?: () => void;
  title?: string;
}


// Get profile path based on user role
const getProfilePath = (role: UserRole, tenantId: string | null): string => {
  const basePath = tenantId ? `/institution/${tenantId}` : '';

  switch (role) {
    case UserRole.SUPER_ADMIN:
      return '/admin/profile';
    case UserRole.DIRECTOR_GENERAL:
      return `${basePath}/dg/profile`;
    case UserRole.PEDAGOGICAL_DIRECTOR:
      return `${basePath}/pedagogie/profile`;
    case UserRole.PROGRAM_HEAD:
      return `${basePath}/filiere/profile`;
    case UserRole.ACADEMIC_COORDINATOR:
      return `${basePath}/coordinator/profile`;
    case UserRole.FINANCIAL_MANAGER:
      return `${basePath}/finance/profile`;
    case UserRole.MARKETING_MANAGER:
      return `${basePath}/marketing/profile`;
    case UserRole.COMMERCIAL_MANAGER:
      return `${basePath}/commercial/profile`;
    case UserRole.BRANCH_MANAGER:
      return `${basePath}/branch/profile`;
    case UserRole.TEACHER:
      return `${basePath}/teacher/profile`;
    case UserRole.STUDENT:
      return `${basePath}/student/profile`;
    case UserRole.PARENT:
      return `${basePath}/parent/profile`;
    default:
      return '/profile';
  }
};

// Get privacy path based on user role
const getPrivacyPath = (role: UserRole, tenantId: string | null): string => {
  const basePath = tenantId ? `/institution/${tenantId}` : '';

  switch (role) {
    case UserRole.SUPER_ADMIN:
      return '/admin/privacy';
    case UserRole.DIRECTOR_GENERAL:
      return `${basePath}/dg/privacy`;
    case UserRole.PEDAGOGICAL_DIRECTOR:
      return `${basePath}/pedagogie/privacy`;
    case UserRole.PROGRAM_HEAD:
      return `${basePath}/filiere/privacy`;
    case UserRole.ACADEMIC_COORDINATOR:
      return `${basePath}/coordinator/privacy`;
    case UserRole.FINANCIAL_MANAGER:
      return `${basePath}/finance/privacy`;
    case UserRole.MARKETING_MANAGER:
      return `${basePath}/marketing/privacy`;
    case UserRole.COMMERCIAL_MANAGER:
      return `${basePath}/commercial/privacy`;
    case UserRole.BRANCH_MANAGER:
      return `${basePath}/branch/privacy`;
    case UserRole.TEACHER:
      return `${basePath}/teacher/privacy`;
    case UserRole.STUDENT:
      return `${basePath}/student/privacy`;
    case UserRole.PARENT:
      return `${basePath}/parent/privacy`;
    default:
      return '/privacy';
  }
};

// Get security path based on user role
const getSecurityPath = (role: UserRole, tenantId: string | null): string => {
  const basePath = tenantId ? `/institution/${tenantId}` : '';

  switch (role) {
    case UserRole.SUPER_ADMIN:
      return '/admin/security';
    case UserRole.DIRECTOR_GENERAL:
      return `${basePath}/dg/security`;
    case UserRole.PEDAGOGICAL_DIRECTOR:
      return `${basePath}/pedagogie/security`;
    case UserRole.PROGRAM_HEAD:
      return `${basePath}/filiere/security`;
    case UserRole.ACADEMIC_COORDINATOR:
      return `${basePath}/coordinator/security`;
    case UserRole.FINANCIAL_MANAGER:
      return `${basePath}/finance/security`;
    case UserRole.MARKETING_MANAGER:
      return `${basePath}/marketing/security`;
    case UserRole.COMMERCIAL_MANAGER:
      return `${basePath}/commercial/security`;
    case UserRole.BRANCH_MANAGER:
      return `${basePath}/branch/security`;
    case UserRole.TEACHER:
      return `${basePath}/teacher/security`;
    case UserRole.STUDENT:
      return `${basePath}/student/security`;
    case UserRole.PARENT:
      return `${basePath}/parent/security`;
    default:
      return '/security';
  }
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick, title }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [showUserMenu, setShowUserMenu] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Menu size={20} className="text-gray-600" />
        </button>

{/* Title removed - displayed in page content instead */}
      </div>

      {/* Center Section - Search */}
      <div className="hidden md:flex flex-1 max-w-md mx-8">
        <div className="relative w-full">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500">
                {user?.fonction || (user?.role ? RoleDisplayNames[user.role] : '')}
              </p>
            </div>
            <ChevronDown size={16} className="text-gray-400 hidden sm:block" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  if (user?.role) {
                    navigate(getProfilePath(user.role, user.tenantId));
                  }
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <User size={16} className="text-gray-400" />
                Mon profil
              </button>

              <button
                onClick={() => {
                  setShowUserMenu(false);
                  if (user?.role) {
                    navigate(getSecurityPath(user.role, user.tenantId));
                  }
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Shield size={16} className="text-gray-400" />
                Sécurité
              </button>

              {/* Parents have no in-app consents (covered by the enrollment
                  contract) — no Confidentialité entry for them */}
              {user?.role !== UserRole.PARENT && (
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    if (user?.role) {
                      navigate(getPrivacyPath(user.role, user.tenantId));
                    }
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Lock size={16} className="text-gray-400" />
                  Confidentialité
                </button>
              )}

              <div className="border-t border-gray-100 mt-1 pt-1">
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut size={16} />
                  Déconnexion
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
